import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { X } from 'lucide-react';
import { COLUMN_DEFINITIONS } from './DocumentsTable';

interface DocumentColumnConfigProps {
  visibleColumns: string[];
  onVisibleColumnsChange: (columns: string[]) => void;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentColumnConfig: React.FC<DocumentColumnConfigProps> = ({
  visibleColumns,
  onVisibleColumnsChange,
  isOpen,
  onClose,
}) => {
  const [tempVisibleColumns, setTempVisibleColumns] = useState<string[]>(visibleColumns);

  // Update temp state when visibleColumns prop changes
  useEffect(() => {
    setTempVisibleColumns(visibleColumns);
  }, [visibleColumns]);

  const handleColumnToggle = (columnKey: string) => {
    setTempVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        // Don't allow removing the last column
        if (prev.length <= 1) {
          return prev;
        }
        return prev.filter(col => col !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  const handleSave = () => {
    onVisibleColumnsChange(tempVisibleColumns);
    onClose();
  };

  const handleCancel = () => {
    setTempVisibleColumns(visibleColumns);
    onClose();
  };

  const handleSelectAll = () => {
    setTempVisibleColumns(Object.keys(COLUMN_DEFINITIONS));
  };

  const handleSelectNone = () => {
    // Keep at least one column (title)
    setTempVisibleColumns(['title']);
  };

  const handleSelectDefault = () => {
    setTempVisibleColumns([
      'title', 'category', 'status', 'creator_name', 'nis2_article', 'nis2_letter', 'updated_at', 'actions'
    ]);
  };

  // Group columns by category
  const columnGroups = {
    basic: ['title', 'description', 'category', 'status', 'version'],
    people: ['creator_name'],
    nis2: ['nis2_chapter', 'nis2_article', 'nis2_letter'],
    metadata: ['tags', 'review_date', 'review_period'],
    dates: ['created_at', 'updated_at'],
    actions: ['actions']
  };

  const groupLabels = {
    basic: 'Základní informace',
    people: 'Lidé',
    nis2: 'NIS2 mapování',
    metadata: 'Metadata',
    dates: 'Datumy',
    actions: 'Akce'
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold">Konfigurace sloupců</h2>
            <p className="text-sm text-gray-600 mt-1">
              Vyberte, které sloupce chcete zobrazit v tabulce dokumentů.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 space-y-4">
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={handleSelectAll}>
              Vybrat vše
            </Button>
            <Button variant="outline" size="sm" onClick={handleSelectNone}>
              Zrušit vše
            </Button>
            <Button variant="outline" size="sm" onClick={handleSelectDefault}>
              Výchozí
            </Button>
          </div>

          {/* Column selection by groups */}
          <div className="space-y-4">
            {Object.entries(columnGroups).map(([groupKey, columns]) => (
              <Card key={groupKey}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    {groupLabels[groupKey as keyof typeof groupLabels]}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {columns.map(columnKey => {
                      const columnDef = COLUMN_DEFINITIONS[columnKey as keyof typeof COLUMN_DEFINITIONS];
                      if (!columnDef) return null;

                      const isVisible = tempVisibleColumns.includes(columnKey);
                      const isLastColumn = tempVisibleColumns.length === 1 && isVisible;

                      return (
                        <div key={columnKey} className="flex items-center space-x-2">
                          <Checkbox
                            id={columnKey}
                            checked={isVisible}
                            onCheckedChange={() => handleColumnToggle(columnKey)}
                            disabled={isLastColumn}
                          />
                          <label
                            htmlFor={columnKey}
                            className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${
                              isLastColumn ? 'opacity-50' : ''
                            }`}
                          >
                            {columnDef.label}
                          </label>
                          <div className="flex gap-1">
                            {columnDef.sortable && (
                              <Badge variant="outline" className="text-xs px-1">
                                Řazení
                              </Badge>
                            )}
                            {columnDef.filterable && (
                              <Badge variant="outline" className="text-xs px-1">
                                Filtr
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Summary */}
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">
                Vybráno {tempVisibleColumns.length} z {Object.keys(COLUMN_DEFINITIONS).length} sloupců
              </div>
              {tempVisibleColumns.length === 1 && (
                <div className="text-sm text-amber-600 mt-1">
                  ⚠️ Musí být vybrán alespoň jeden sloupec
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-2 p-6 border-t">
          <Button variant="outline" onClick={handleCancel}>
            Zrušit
          </Button>
          <Button onClick={handleSave}>
            Uložit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentColumnConfig;
